import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh,
  Fullscreen,
  ExitToApp,
  Settings,
  Warning
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import dataConnectionService from '../services/dataConnectionService';

interface PanelSettings {
  url: string;
  title: string;
  allowNavigation: boolean;
  roleBasedUrls?: {
    [role: string]: {
      url: string;
      title: string;
    };
  };
}

const Panel: React.FC = () => {
  const { user } = useAuth();
  const [panelSettings, setPanelSettings] = useState<PanelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [tempUrl, setTempUrl] = useState('');

  // Panel ayarlarını yükle
  useEffect(() => {
    loadPanelSettings();
  }, []);

  const loadPanelSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Data bağlantısı kontrolü
      const isDataConnected = dataConnectionService.isConnected();
      
      if (!isDataConnected) {
        setError('Panel ayarları için veri bağlantısı gerekli. Lütfen admin ile iletişime geçin.');
        setLoading(false);
        return;
      }

      // Panel ayarlarını veri kaynağından yükle
      const settingsResult = await dataConnectionService.getPanelSettings();
      
      if (settingsResult.success && settingsResult.data) {
        setPanelSettings(settingsResult.data);
      } else {
        setError('Panel ayarları bulunamadı. Lütfen admin ile iletişime geçin.');
      }
    } catch (error) {
      console.error('Panel ayarları yükleme hatası:', error);
      setError('Panel ayarları yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcının rolüne göre panel URL'ini belirle
  const getCurrentPanelUrl = () => {
    if (!panelSettings || !user) return '';
    
    // Rol bazlı URL varsa onu kullan
    if (panelSettings.roleBasedUrls && panelSettings.roleBasedUrls[user.role]) {
      return panelSettings.roleBasedUrls[user.role].url;
    }
    
    // Yoksa genel URL'i kullan
    return panelSettings.url;
  };

  // Kullanıcının rolüne göre panel başlığını belirle
  const getCurrentPanelTitle = () => {
    if (!panelSettings || !user) return 'Panel';
    
    // Rol bazlı başlık varsa onu kullan
    if (panelSettings.roleBasedUrls && panelSettings.roleBasedUrls[user.role]) {
      return panelSettings.roleBasedUrls[user.role].title;
    }
    
    // Yoksa genel başlığı kullan
    return panelSettings.title;
  };

  const handleRefresh = () => {
    if (panelSettings) {
      const iframe = document.getElementById('panel-iframe') as HTMLIFrameElement;
      if (iframe) {
        // eslint-disable-next-line no-self-assign
        iframe.src = iframe.src;
      }
    }
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleExitAttempt = () => {
    setShowExitDialog(true);
  };

  const handleConfirmExit = () => {
    // Panel sayfasından çıkışı engelle - sadece admin çıkabilir
    if (user?.role === 'admin') {
      window.history.back();
    } else {
      setShowExitDialog(false);
      // Normal kullanıcılar çıkamaz
    }
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
  };

  const handleOpenSettings = () => {
    if (panelSettings) {
      setTempUrl(panelSettings.url);
      setSettingsDialogOpen(true);
    }
  };

  const handleSaveSettings = async () => {
    if (!tempUrl.trim()) return;

    try {
      const updatedSettings: PanelSettings = {
        ...panelSettings!,
        url: tempUrl.trim()
      };

      const result = await dataConnectionService.savePanelSettings(updatedSettings);
      
      if (result.success) {
        setPanelSettings(updatedSettings);
        setSettingsDialogOpen(false);
        // Sayfayı yenile
        window.location.reload();
      } else {
        setError('Panel ayarları kaydedilemedi: ' + result.message);
      }
    } catch (error) {
      console.error('Panel ayarları kaydetme hatası:', error);
      setError('Panel ayarları kaydedilemedi.');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Panel yükleniyor...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={loadPanelSettings}
          startIcon={<Refresh />}
        >
          Tekrar Dene
        </Button>
      </Box>
    );
  }

  if (!panelSettings) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          Panel ayarları bulunamadı. Lütfen admin ile iletişime geçin.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Panel Başlığı ve Kontroller */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" component="h1">
          {getCurrentPanelTitle()}
        </Typography>
        
        <Box display="flex" gap={1}>
          {user?.role === 'admin' && (
            <Tooltip title="Panel Ayarları">
              <IconButton onClick={handleOpenSettings} size="small">
                <Settings />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Yenile">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={isFullscreen ? "Küçült" : "Tam Ekran"}>
            <IconButton onClick={handleFullscreen} size="small">
              <Fullscreen />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Çıkış">
            <IconButton onClick={handleExitAttempt} size="small" color="error">
              <ExitToApp />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Panel İçeriği - iframe */}
      <Box
        sx={{
          height: 'calc(100% - 73px)',
          width: '100%',
          position: 'relative'
        }}
      >
        <iframe
          id="panel-iframe"
          src={getCurrentPanelUrl()}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: isFullscreen ? 0 : '8px'
          }}
          title={getCurrentPanelTitle()}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      </Box>

      {/* Çıkış Onay Dialog'u */}
      <Dialog open={showExitDialog} onClose={handleCancelExit}>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            Panel'den Çıkış
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {user?.role === 'admin' 
              ? 'Panel\'den çıkmak istediğinizden emin misiniz?'
              : 'Panel\'den çıkış yapamazsınız. Lütfen admin ile iletişime geçin.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExit}>
            İptal
          </Button>
          {user?.role === 'admin' && (
            <Button onClick={handleConfirmExit} color="error" variant="contained">
              Çıkış Yap
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Panel Ayarları Dialog'u */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => setSettingsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Panel Ayarları</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Panel URL"
            value={tempUrl}
            onChange={(e) => setTempUrl(e.target.value)}
            margin="normal"
            placeholder="https://example.com"
            helperText="Panel'de görüntülenecek web sitesinin URL'sini girin"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>
            İptal
          </Button>
          <Button onClick={handleSaveSettings} variant="contained">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Panel;
